import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Heart, Film, ArrowRight } from "lucide-react";
import documentaryPoster from "@/assets/sweet-reunion-poster.jpeg";

export const Documentary = () => {
  return (
    <div id="documentary" className="py-20 bg-gradient-to-br from-story-sudan/20 via-background to-story-germany/20">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Film className="w-6 h-6 text-story-hope" />
            <span className="text-story-hope font-semibold text-sm uppercase tracking-wide">The Documentary</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-foreground mb-6">Witness A Miracle</h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            A boy who couldn't walk. A young woman who believed he could. 26 years of wondering. 
            One Facebook search that changed everything.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <Link to="/the-film" className="block relative rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src={documentaryPoster}
                alt="A Sweet Reunion - 26 Years Apart Documentary Poster"
                className="w-full h-auto transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/30">
                <Button size="lg" className="bg-white/90 text-black hover:bg-white gap-2">
                  <ArrowRight className="w-5 h-5" />
                  View Film Details
                </Button>
              </div>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            <Card className="border-2 border-story-hope/20 bg-gradient-to-br from-card to-muted/30">
              <CardContent className="p-8 space-y-6">
                <div className="space-y-4">
                  <h3 className="font-serif text-2xl md:text-3xl text-foreground">
                    This Is More Than a Reunion
                  </h3>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    This is about a <span className="text-story-hope font-semibold">miracle</span> that defied all odds. 
                    A disabled refugee boy from war-torn Sudan who learned to walk in a foreign land. 
                    A compassionate social worker whose first job shaped her entire life's calling.
                  </p>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    For 26 years, they carried each other in their hearts. Tachen wondered if Bettina 
                    ever thought of him. Bettina searched for her "lost boy" countless times, never giving up hope.
                  </p>
                  <p className="text-lg text-story-peace leading-relaxed font-medium">
                    On January 22, 2020, after a trauma workshop, Bettina searched one more time. 
                    There he was. His face unchanged. Her heart recognized him instantly.
                  </p>
                </div>

                <div className="pt-6 border-t border-border/50">
                  <h4 className="text-xl font-serif text-foreground mb-4 flex items-center gap-2">
                    <Heart className="w-5 h-5 text-story-hope" />
                    Help Us Make This Happen
                  </h4>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Your support will bring Tachen back to Friedensdorf Peace Village—the place where 
                    his life changed forever. Together, they'll inspire the children there today, showing 
                    them that healing is real, that hope endures, and that miracles do happen.
                  </p>
                  <p className="text-foreground font-semibold text-lg mb-6">
                    We're documenting this extraordinary reunion. Be part of a story that will inspire 
                    the world to believe in the power of compassion.
                  </p>
                  <Button 
                    size="lg"
                    className="w-full bg-story-hope hover:bg-story-hope/90 text-black font-bold text-lg h-auto py-4"
                    onClick={() => document.getElementById('fund-project')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    <Heart className="w-5 h-5 mr-2" />
                    Help Make This Miracle Complete
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
